import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCertificateTemplates } from '@share/store/slices';
import type { ICertificateTemplatesParams, ICertificateTemplate } from '@share/common-types';
import { FunctionalRootState } from '@share/utils';
import { useCertificateClubSiteId } from '@share/components';

const DEFAULT_PAGE_SIZE = 6;

export interface UseCertificateTemplatesParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  orderBy?: string;
  sortOrder?: string;
  itemTypes?: ICertificateTemplatesParams['itemTypes'];
}

export interface UseCertificateTemplatesResult {
  items: ICertificateTemplate[];
  totalCount: number;
  loading: boolean;
  error: string;
  refetch: () => void;
}

export function useCertificateTemplates(
  params: UseCertificateTemplatesParams = {},
): UseCertificateTemplatesResult {
  const {
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    search,
    orderBy,
    sortOrder,
    itemTypes,
  } = params;

  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(
    (state: FunctionalRootState) => state.certificateTemplatesSearchStore,
  );
  const organizationId = useCertificateClubSiteId();

  const requestParams: ICertificateTemplatesParams = useMemo(
    () => ({
      pageNumber,
      pageSize,
      search,
      orderBy,
      sortOrder,
      itemTypes,
      clubSiteIds: organizationId ? [organizationId] : [],
    }),
    [pageNumber, pageSize, search, orderBy, sortOrder, itemTypes, organizationId],
  );

  const refetch = useCallback(() => {
    dispatch(fetchCertificateTemplates(requestParams));
  }, [dispatch, requestParams]);

  useEffect(() => {
    dispatch(fetchCertificateTemplates(requestParams));
  }, [dispatch, requestParams]);

  return {
    items: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    loading,
    error,
    refetch,
  };
}
